import { FALLBACK_EXCHANGE_CATALOG } from '@/lib/exchangeCatalog'
import type { NamedPreset, PresetPreferenceKey } from '@/lib/namedPresets'

// User and Auth types
export interface UserPreferences extends Partial<Record<PresetPreferenceKey, NamedPreset<unknown>[]>> {
  alert_notifications_enabled?: boolean
  screener_display_exchanges?: string[]
  screener_discovery_exchanges?: string[]
  screener_discovery_filters?: ScreenerDiscoveryFilterDefaults
  screener_parse_filters?: ScreenerParseFilterDefaults
  [key: string]: unknown
}

export interface TelegramAccount {
  user_id: string
  oidc_subject?: string | null
  username?: string | null
  display_name?: string | null
  photo_url?: string | null
  chat_id?: string | null
  is_primary: boolean
  linked_at?: string | null
  last_login_at?: string | null
}

export interface User {
  username: string
  role: 'admin' | 'user'
  permissions: Record<string, boolean>
  pushover_user_key?: string
  telegram_nickname?: string
  telegram_nicknames?: string[]
  telegram_chat_id?: string
  telegram_chat_ids?: string[]
  telegram_accounts?: TelegramAccount[]
  has_password?: boolean
  availability_status?: 'online' | 'offline'
  availability_changed_at?: string | null
  preferences?: UserPreferences
  csrf_token?: string
}

export interface Session {
  username: string
  role: string
  permissions: Record<string, boolean>
  csrf_token: string
}

// Alert types
export type OIThresholdUnit = 'percent' | 'usdt' | 'coins'

export interface Alert {
  id: string
  name: string
  exchanges: string[]
  exchange: string
  oi_threshold_pct: number
  oi_threshold_unit?: OIThresholdUnit
  oi_threshold_value?: number
  oi_period: string
  volume_period: string
  enabled: boolean
  filters: {
    min_period_volume_usdt: number
  }
  ignore: string[]
  required_tags: string[]
  exclude_tags: string[]
  symbol?: string
  enable_telegram: boolean
  enable_pushover: boolean
  oi_direction?: 'increase' | 'decrease' | 'both'
  telegram?: {
    chat_id: string
    reply_to_message_id?: string
  }
  pushover?: {
    group_key: string
  }
}

export interface AlertTrigger {
  alert_id?: string
  symbol: string
  exchange: string
  oi_change_pct?: number
  price?: number
  stage?: string
  volume_series?: number[]
  volume_period?: string
  market_type?: string
  timestamp?: number
}

export interface AlertSnapshot {
  symbols: Record<string, AlertSnapshotItem[]>
}

export interface AlertSnapshotItem {
  exchange: string
  oi_change?: number
  volume_24h?: number
  price_change_24h?: number
  funding_rate?: number
  timestamp: number
}

// OI Alerts status
export interface OIAlertsStatus {
  running: boolean
  alerts: Alert[]
  telegram?: {
    bot_token: string
    reply_to_message_id?: string
  }
  ignore: string[]
  triggers_count?: number
}

// Notification config types
export interface TelegramNotificationConfig {
  chat_id?: string
  bot_token?: string
  reply_to_message_id?: number | string | null
  enabled?: boolean  // Used for removal/retrigger configs
}

export interface PushoverNotificationConfig {
  group_key?: string
  notify_users?: string[]  // List of user IDs to notify
  enabled?: boolean  // Used for removal/retrigger configs
}

// Per-exchange notification override
export interface ExchangeNotificationOverride {
  enable_telegram?: boolean
  enable_pushover?: boolean
  telegram?: TelegramNotificationConfig
  pushover?: PushoverNotificationConfig
  enable_removal_notifications?: boolean
  removal_telegram?: TelegramNotificationConfig
  removal_pushover?: PushoverNotificationConfig
  enable_retrigger_notifications?: boolean
  retrigger_telegram?: TelegramNotificationConfig
  retrigger_pushover?: PushoverNotificationConfig
}

// Orderbook types
export interface OrderbookAlert {
  id: string
  name: string
  exchanges: string[]
  market_type?: 'spot' | 'futures'
  precision?: string
  min_usd_value?: number  // Backend field name
  min_order_usdt?: number  // Legacy field name (for backward compatibility)
  grace_period_seconds?: number
  retrigger_interval_days?: number
  retrigger_hour_utc?: number
  price_tolerance_pct?: number
  update_notification_pct?: number | null
  max_deviation_pct?: number | null
  enabled: boolean
  ignore: string[]
  required_tags?: string[]
  required_tags_any?: string[]
  exclude_tags?: string[]
  tag_expression?: string
  // Detection notifications
  enable_telegram?: boolean
  enable_pushover?: boolean
  telegram?: TelegramNotificationConfig
  pushover?: PushoverNotificationConfig
  // Removal notifications
  enable_removal_notifications?: boolean
  removal_telegram?: TelegramNotificationConfig
  removal_pushover?: PushoverNotificationConfig
  // Retrigger notifications
  enable_retrigger_notifications?: boolean
  retrigger_telegram?: TelegramNotificationConfig
  retrigger_pushover?: PushoverNotificationConfig
  // Per-exchange notification overrides
  exchange_notifications?: Record<string, ExchangeNotificationOverride>
}

export interface OrderbookStatus {
  running: boolean
  orderbook_alerts: OrderbookAlert[]
  tracked_count?: number
  notified_count?: number
  orderbook_settings?: Record<string, unknown>
}

export interface TrackedOrder {
  symbol: string
  exchange: string
  side: 'bid' | 'ask'
  price: number
  usd_value: number
  size_usdt?: number // Alias for usd_value
  first_seen_ts?: number
  last_seen_ts: number
  first_seen?: number // Alias
  last_seen?: number // Alias
  market_type?: string
  base?: string
  tags?: string[]
  confirmed?: boolean
  alert_id?: string
  coin_size?: number
}

// Mark Price types
export interface MarkPriceAlert {
  id: string
  name: string
  exchanges: string[]
  threshold_pct: number
  period: string
  enabled: boolean
  required_tags?: string[]
  required_tags_any?: string[]
  exclude_tags?: string[]
  tag_expression?: string
  enable_telegram?: boolean
  enable_pushover?: boolean
  telegram?: TelegramNotificationConfig
  pushover?: PushoverNotificationConfig
}

export interface MarkPriceStatus {
  running: boolean
  mark_price_alerts: MarkPriceAlert[]
  triggers_count?: number
  ignore?: string[]
}

export interface MarkPriceTrigger {
  symbol: string
  exchange: string
  change_pct: number
  mark_price: number
  timestamp: number
}

// Funding types
export interface FundingAlert {
  id: string
  name: string
  exchanges: string[]
  enabled: boolean
  funding_rate_threshold: number
  time_before_funding_minutes: number
  cooldown_seconds: number
  required_tags?: string[]
  required_tags_any?: string[]
  exclude_tags?: string[]
  tag_expression?: string
  enable_telegram?: boolean
  enable_pushover?: boolean
  telegram?: TelegramNotificationConfig
  pushover?: PushoverNotificationConfig
}

export interface FundingStatus {
  running: boolean
  funding_alerts: FundingAlert[]
  triggers_count?: number
  ignore?: string[]
}

// Price Difference types
export interface PriceDifferenceAlert {
  id: string
  name: string
  exchanges: string[]
  threshold_pct: number
  enabled: boolean
  required_tags?: string[]
  required_tags_any?: string[]
  exclude_tags?: string[]
  tag_expression?: string
  enable_telegram?: boolean
  enable_pushover?: boolean
  telegram?: TelegramNotificationConfig
  pushover?: PushoverNotificationConfig
}

export interface PriceDifferenceStatus {
  status: string
  message?: string
  triggers_count?: number
}

export interface PriceDifferenceSettings {
  price_difference_alerts: PriceDifferenceAlert[]
  ignore?: string[]
}

// Splash Alert types
export interface SplashAlert {
  id: string
  name: string
  exchanges: string[]
  enabled: boolean
  filters?: {
    splash_lookback_minutes?: number
    splash_min_change_pct?: number
    min_vol24_usdt?: number
  }
  splash_lookback_minutes?: number
  splash_min_change_pct?: number
  cooldown_seconds?: number
  ignore?: string[]
  required_tags?: string[]
  exclude_tags?: string[]
  tag_expression?: string
  enable_telegram?: boolean
  enable_pushover?: boolean
  telegram?: TelegramNotificationConfig
  pushover?: PushoverNotificationConfig
}

export interface SplashStatus {
  running: boolean
  splash_alerts: SplashAlert[]
  triggers_count?: number
  ignore?: string[]
}

export interface SplashSettings {
  splash_alerts: SplashAlert[]
  ignore?: string[]
}

// Spread Alert types
export interface SpreadAlert {
  id: string
  name: string
  exchanges: string[]
  enabled: boolean
  filters?: {
    min_diff_pct?: number
    spread_update_pct?: number
    spread_group_tolerance_pct?: number
    min_vol24_usdt?: number
  }
  min_diff_pct?: number
  spread_update_pct?: number
  spread_group_tolerance_pct?: number
  cooldown_seconds?: number
  ignore?: string[]
  required_tags?: string[]
  exclude_tags?: string[]
  tag_expression?: string
  enable_telegram?: boolean
  enable_pushover?: boolean
  telegram?: TelegramNotificationConfig
  pushover?: PushoverNotificationConfig
}

export interface SpreadStatus {
  running: boolean
  spread_alerts: SpreadAlert[]
  triggers_count?: number
  ignore?: string[]
}

export interface SpreadSettings {
  spread_alerts: SpreadAlert[]
  ignore?: string[]
}

// Tag types
export interface TagEntry {
  symbol: string
  tags: string[]
}

// Alias types
export interface AliasGroup {
  [exchange: string]: string
}

export interface Aliases {
  [groupName: string]: AliasGroup
}

// Overview types for dashboard
export interface OverviewAlert {
  id: string
  type: 'oi' | 'orderbook' | 'mark_price' | 'price_diff' | 'funding' | 'splash' | 'spread'
  symbol: string
  exchange: string
  value: number
  timestamp: number
  details?: Record<string, unknown>
}

// Exchange info
export type Exchange = string

export const FALLBACK_EXCHANGES: Exchange[] = FALLBACK_EXCHANGE_CATALOG.map((item) => item.name)

export const EXCHANGE_DISPLAY_NAMES = Object.fromEntries(FALLBACK_EXCHANGE_CATALOG.map((item) => [item.name, item.display_name]))
export const EXCHANGE_PRICE_NOTES = Object.fromEntries(FALLBACK_EXCHANGE_CATALOG.map((item) => [item.name, item.price_notes || ""]))

export const EXCHANGE_COLORS: Record<string, string> = {
  binance: '#f0b90b',
  binance_alpha: '#eab308',
  bitget: '#00c896',
  bybit: '#f7a600',
  okx: '#000000',
  gate: '#17e1ae',
  kucoin: '#23af91',
  mexc: '#1972f4',
  asterdex: '#f59e0b',
  hyperliquid: '#00ff88',
  lighter: '#38bdf8',
  bitunix: '#a78bfa',
  whitebit: '#e4e4e7',
  bingx: '#2b6cff',
  lbank: '#a7e92f',
  kraken: '#8857ff',
  upbit: '#3377ff',
  coinbase: '#1652f0',
  bithumb: '#f97316',
  bitfinex: '#16b157',
  weex: '#f8ed1b',
}

export type ExchangeFeature =
  | 'spot_ticker'
  | 'futures_ticker'
  | 'mark_price'
  | 'funding_rate'
  | 'open_interest'
  | 'orderbook'
  | 'trades'
  | 'klines'

export interface ExchangeOption {
  name: string
  display_name: string
  enabled: boolean
  features: Record<ExchangeFeature, boolean>
  binance_compatible: boolean
  feature_markets?: Partial<Record<ExchangeFeature, Array<'spot' | 'futures'>>>
  feature_notes?: Partial<Record<ExchangeFeature, string>>
  price_notes?: string
}

export interface ExchangeOptionsResponse {
  enabled: string[]
  all: string[]
  exchanges: ExchangeOption[]
  by_feature: Record<ExchangeFeature, string[]>
}

// Alerts Overview types
export interface AlertOverviewItem {
  alert_type: 'oi' | 'orderbook' | 'mark_price' | 'price_diff' | 'funding' | 'splash' | 'spread'
  timestamp: number
  first_seen_ts?: number
  last_updated_ts?: number
  exchange: string
  symbol: string
  base?: string
  alert_name?: string
  tags: string[]
  status?: 'new' | 'stale' | 'suspended' | 'update'
  // OI alert fields
  oi_change_pct?: number
  oi_period?: string
  volume_series?: number[]
  // Orderbook alert fields
  side?: 'bid' | 'ask'
  price?: number
  size?: number
  usd_value?: number
  deviation_pct?: number
  market_type?: 'spot' | 'futures'
  // Mark price fields
  direction?: string
  threshold_pct?: number
  // Price diff fields
  max_diff_pct?: number
  exchange_prices?: Record<string, number>
  outlier_exchanges?: string[]
  sorted_exchanges?: Array<[string, number]>
  // Cooldown info
  cooldown_until_ts?: number
  cooldown_seconds?: number
}

export interface AlertsOverviewParams {
  exchange?: string
  alert_type?: string
  tags?: string
  time_range?: string
  alert_age?: string
  alert_names?: string
  market_type?: string
  min_value?: number
  max_value?: number
}

export interface AlertsOverviewResponse {
  alerts: AlertOverviewItem[]
  oi_alerts: AlertOverviewItem[]
  orderbook_alerts: AlertOverviewItem[]
  mark_price_alerts: AlertOverviewItem[]
  price_diff_alerts: AlertOverviewItem[]
  summary: {
    total_count: number
    by_type: Record<string, number>
    by_exchange: Record<string, number>
    by_symbol: Record<string, number>
  }
  alert_names: string[]
  unique_tags: string[]
}

// State tools (admin)
export interface StateComponent {
  id: string
  label?: string
  category?: string
  has_state?: boolean
  item_count?: number
  worker_count?: number
  memory_size?: number
  memory_size_formatted?: string
  disk_size?: number
  disk_size_formatted?: string
  total_size?: number
  total_size_formatted?: string
  last_updated?: string | null
  requires_restart?: string[]
}

export interface StateCategory {
  name: string
  components: StateComponent[]
  total_items?: number
  total_size?: number
  total_size_formatted?: string
  has_state?: boolean
}

export interface StateStatusResponse {
  components: StateComponent[]
  categories: StateCategory[]
}

export interface StateClearResult {
  ok: boolean
  error?: string
  files_deleted?: number
}

export interface StateClearResponse {
  ok: boolean
  error?: string
  results?: Record<string, StateClearResult>
  restart_results?: Record<string, { ok: boolean; error?: string }>
  components?: StateComponent[]
  categories?: StateCategory[]
}

// Position Discussion types
export interface PositionActivity {
  type: 'updated' | 'closed' | 'reopened' | 'notification'
  actor: string
  created_at: string
  message?: string
  result?: 'profit' | 'loss'
  urgency?: number
  users_added?: string[]
  users_removed?: string[]
  users_notified?: string[]
}

export interface Position {
  id: string
  symbol: string
  status: 'open' | 'closed'
  direction?: 'long' | 'short' | null
  created_by: string
  created_at: string
  closed_at?: string
  closed_by?: string
  closed_reason?: string
  closed_result?: 'profit' | 'loss'
  decision_reason?: string
  users_in_position: string[]
  topic_id?: string
  source_alert_data?: Record<string, unknown>
  source_message_link?: string
  entry_price?: number | null
  update_history?: PositionActivity[]
}

export interface PositionUser {
  id: string
  username: string
  name: string
  pushover_user_key?: string
  telegram_nickname?: string
  telegram_nicknames?: string[]
  telegram_chat_id?: string
  telegram_chat_ids?: string[]
}

export interface CreatePositionPayload {
  symbol: string
  direction?: 'long' | 'short' | null
  decision_reason: string
  users_in_position?: string[]
  source_alert_data?: Record<string, unknown>
  source_message_link?: string
}

export interface UpdatePositionPayload {
  users_to_add?: string[]
  users_to_remove?: string[]
  update_message?: string
}

export interface ReopenPositionPayload {
  reopen_reason?: string
}

// Market Data types (for Coin Overview)
export interface OpenInterestData {
  current: number | null
  prev: number | null
  delta: number | null
  usdt_current: number | null
  usdt_prev: number | null
  usdt_delta: number | null
}

export interface MarketRecord {
  exchange: string
  type: 'SPOT' | 'FUTURES'
  symbol: string
  exchange_symbol?: string
  price: number | null
  volume_current: number | null
  volume_prev: number | null
  volume_delta?: number | null
  volume_side_current?: string
  volume_side_prev?: string
  series_quote_volumes?: number[]
  volume_series?: number[]
  open_interest?: OpenInterestData
  bias?: string
  change_current?: string | null
  status: string
}

export interface ExchangeMarketData {
  exchange: string
  spot?: MarketRecord
  futures?: MarketRecord
}

export interface CoinMarketData {
  symbol: string
  baseSymbol: string
  exchanges: ExchangeMarketData[]
  totalVolumeCurrent: number | null
  totalVolumePrev: number | null
  volumeChangePercent: number | null
  totalOICurrent: number
  totalOIPrev: number
  oiChangePercent: number
  avgPrice: number | null
  exchangeCount: number
  spotCount: number
  futuresCount: number
}

export interface CoinOverviewParams {
  symbols: string[]
  period: string
  includeSpot: boolean
  includeFutures: boolean
  exchanges: string[]
}

// Screener types
export interface ScreenerOHLC {
  open: number
  high: number
  low: number
  close: number
}

export interface ScreenerCandle extends ScreenerOHLC {
  timestamp: number
  volume: number
}

export interface ScreenerOIDataPoint {
  timestamp: number
  value: number
  /** Observed intrabucket range; absent when retained history has no endpoints. */
  ohlc?: ScreenerOHLC
  valueUsdt?: number | null
  price?: number | null
  quality?: 'fresh' | 'carried_forward' | 'aggregate' | string
  observedAt?: number | null
  sourceTimestamp?: number | null
  valueOriginAt?: number | null
  sampleKind?: 'live' | 'background' | 'aggregate' | string
}

export interface ScreenerVolumeDataPoint {
  /** UTC bucket start in Unix seconds; volume covers [timestamp, timestamp + interval). */
  timestamp: number
  volumeCoins: number | null
  volumeUsdt: number | null
  /** Native quote notional; USDC/USD must retain their actual currency label. */
  volumeQuote?: number | null
  quoteCurrency?: string | null
  updatedAt?: number | null
  /** False while the bucket is open or its stored coverage is incomplete. */
  complete: boolean
}

/** Buy minus sell base quantity, accumulated from the loaded history's zero baseline. */
export interface ScreenerCVDCandle {
  timestamp: number
  open: number | null
  high: number | null
  low: number | null
  close: number | null
  delta: number | null
  complete: boolean
  /** Finest retained trade samples used to reconstruct this candle. */
  resolution: '1s' | '1m' | null
  updatedAt?: number | null
}

export interface ScreenerExchangeData {
  candles: ScreenerCandle[]
  oi_current: number | null  // OI in coins
  oi_current_usdt: number | null  // OI in USDT
  oi_history?: ScreenerOIDataPoint[]
  oi_updated_at?: number | null
  oi_observed_at?: number | null
  oi_quality?: string
  oi_sample_kind?: string
  oi_source_age_seconds?: number | null
  /** Latest collected funding snapshot; rates are fractions (0.0001 = 0.01%). */
  funding_rate?: number | null
  /** Next funding time and ticker snapshot time, in Unix seconds. */
  funding_time?: number | null
  funding_updated_at?: number | null
  /** Futures trade volume, separate from OI so display fills never copy volume. */
  volume_history?: ScreenerVolumeDataPoint[]
  volume_interval?: string
  volume_updated_at?: number | null
  /** Replace the whole CVD history on refresh: late trades revise subsequent candles. */
  cvd_history?: ScreenerCVDCandle[]
  cvd_interval?: string
  cvd_unit?: 'coins'
  cvd_baseline_ts?: number | null
  cvd_updated_at?: number | null
  cvd_has_gaps?: boolean
  cvd_error?: string | null
  last_price: number | null
  volume_24h: number | null
  updated_at: number | null
  market?: 'spot' | 'futures' | string
  orderbook?: {
    tick_size?: number | null
    market?: string
    updated_at?: number | null
    bids?: Array<{ price: number; coin_size: number; usdt_size: number }>
    asks?: Array<{ price: number; coin_size: number; usdt_size: number }>
  }
}

export interface ScreenerOrderbookSource {
  /** Native price increment when supplied by the data source. */
  tick_size?: number | null
  id: string
  exchange: string
  market: string
  updated_at?: number | null
  bids?: Array<{ price: number; coin_size: number; usdt_size: number }>
  asks?: Array<{ price: number; coin_size: number; usdt_size: number }>
}

export interface ScreenerTickerData {
  history_window?: {
    from_ts: number
    to_ts: number
    earliest_ts: number | null
    latest_ts: number | null
    has_earlier: boolean
    has_later: boolean
    limit: number
    truncated: boolean
  }
  symbol: string
  query_symbol?: string
  updated_at: number | null
  /** Last completed 5s bucket covered by the history query, capped by its requested end. */
  oi_history_until?: number | null
  oi_interval?: '5s' | '1m' | '5m' | string
  oi_include_ohlc?: boolean
  price_interval?: string
  live_oi?: {
    enabled: boolean
    requested: string[]
    mode: 'continuous'
    collecting: string[]
    unsupported: string[]
    disabled: string[]
    unavailable: string[]
  }
  available_exchanges: string[]
  orderbook_sources?: ScreenerOrderbookSource[]
  exchanges: Record<string, ScreenerExchangeData>
}

export interface ScreenerTicker {
  symbol: string
  available_exchanges: string[]
  added_at: number | null
  updated_at: number | null
}

export interface ScreenerTimeframe {
  mode: 'live' | 'relative' | 'absolute'
  /** Relative: seconds before now (e.g. 1800 = 30m, 14400 = 4h) */
  relative_seconds?: number
  /** Absolute: Unix epoch seconds */
  from_ts?: number
  to_ts?: number
}

export interface ScreenerParseFilters {
  exchanges: string[]
  markets: string[]
}

export interface ScreenerParseFilterDefaults {
  chart_filters: ScreenerParseFilters
  orderbook_filters: ScreenerParseFilters
}

export interface ScreenerSettings {
  period: string
  exchanges: string[]
  markets?: string[]
  chart_filters?: ScreenerParseFilters
  orderbook_filters?: ScreenerParseFilters
  candle_limit: number
  timeframe?: ScreenerTimeframe
}

export interface ScreenerState {
  tickers: ScreenerTicker[]
  settings: ScreenerSettings
}

export type ScreenerDiscoveryChangePeriod = '15m' | '1h' | '24h'
export type ScreenerDiscoverySort = 'symbol' | 'last_price' | 'oi_usd'
  | `${'oi' | 'matching'}_change_${'pct' | 'usd'}_${ScreenerDiscoveryChangePeriod}`

export type ScreenerDiscoveryChanges = Partial<Record<
  `oi_change_${'coins' | 'pct' | 'usd'}_${ScreenerDiscoveryChangePeriod}`, number | null
>>

export interface ScreenerDiscoveryFilters {
  exchanges: string[]
  search: string
  filterScope: 'combined' | 'exchange'
  changePeriod: ScreenerDiscoveryChangePeriod
  changeDirection: 'either' | 'increase' | 'decrease'
  minMatchingExchanges: number
  minOiUsd: number
  minAbsChangePct: number
  minAbsChangeUsd: number
  includeTags: string[]
  excludeTags: string[]
  sortBy: ScreenerDiscoverySort
  sortDirection: 'asc' | 'desc'
}

export type ScreenerDiscoveryFilterDefaults = Omit<ScreenerDiscoveryFilters, 'exchanges' | 'search'>

export interface ScreenerDiscoveryExchangeDetail extends ScreenerDiscoveryChanges {
  exchange: string
  last_price?: number | null
  oi_coins: number
  oi_usd: number | null
  latest_ts: number
  previous_ts?: number | null
}

export interface ScreenerDiscoveryItem extends ScreenerDiscoveryChanges, Partial<Record<
  `matching_change_${'pct' | 'usd'}_${ScreenerDiscoveryChangePeriod}`, number | null
>> {
  symbol: string
  last_price?: number | null
  exchanges: string[]
  tags: string[]
  oi_coins: number
  oi_usd: number
  change_exchange_count: number
  latest_ts: number
  previous_ts?: number
  exchange_details?: ScreenerDiscoveryExchangeDetail[]
  matching_exchanges?: string[]
  matching_exchange_count?: number
}

export interface ScreenerDiscoveryResponse {
  ok: boolean
  change_period?: ScreenerDiscoveryChangePeriod
  items: ScreenerDiscoveryItem[]
  available_tags: string[]
  selected_exchanges?: string[]
  as_of?: number
  error?: string
}

// Admin Debug Snapshot types
export interface DebugSystemInfo {
  pid: number
  memory_rss_mb: number
  memory_vms_mb: number
  cpu_percent: number
  threads: number
  create_time: number
  uptime_seconds: number
  error?: string
}

export interface DebugWsExchangeHealth {
  connected: boolean
  subscriptions?: number
  messages_received?: number
  errors?: number
  circuit_breaker_open?: boolean
  rest_fallback?: boolean
  [key: string]: unknown
}

export interface DebugWsStats {
  total_handlers: number
  connected_handlers: number
  total_subscriptions: number
  total_messages: number
  total_errors: number
  circuit_breakers_open: number
  error?: string
}

export interface DebugWorkerInfo {
  pid: number | null
  healthy: boolean
  restart_count: number
  uptime_seconds: number
}

export interface DebugAlertWorkers {
  running?: boolean
  uptime_seconds?: number
  num_workers?: number
  total_restarts?: number
  workers?: Record<string, DebugWorkerInfo>
  status?: string
  error?: string
}

export interface DebugProcessInfo {
  name: string
  type: 'thread' | 'asyncio_task' | 'process'
  status: string
  pid: number | null
  healthy: boolean
  details?: Record<string, unknown>
}

export interface DebugSnapshot {
  snapshot_at: string
  snapshot_ts: number
  system: DebugSystemInfo
  ws_health: Record<string, DebugWsExchangeHealth> | { status: string; error?: string }
  ws_stats: DebugWsStats
  alert_workers: DebugAlertWorkers
  processes?: DebugProcessInfo[]
}

// Coin Overview — Position History types
export interface PositionHistoryNote {
  id: string | number
  symbol: string
  message: string
  added_by: string
  created_at: string
  photo_file_id?: string
}

export interface PositionHistoryEntry {
  id: string
  symbol: string
  status: 'open' | 'closed'
  created_by: string
  created_at: string
  closed_at?: string
  closed_by?: string
  closed_reason?: string
  closed_result?: 'profit' | 'loss'
  decision_reason?: string
  direction?: 'long' | 'short' | null
  users_in_position: string[]
  alert_counts: Array<{ exchange: string; alert_type: string; count: number }>
  oi_snapshots: Array<{
    id: string
    exchange: string
    symbol: string
    snapshot_type: string
    created_at: string
  }>
}

export interface PositionHistoryData {
  ticker: string
  positions: PositionHistoryEntry[]
  notes: PositionHistoryNote[]
}

// Coin Overview — Ticker Info (AI Research)
export interface TickerInfo {
  ticker: string
  info?: string
  info_ru?: string
  contract_addresses?: string
  researched_at?: number
  updated_at?: number
}

// Coin Overview — Analyze Result
export interface AnalyzeMarketEntry {
  exchange: string
  price: number
  mark_price?: number
  funding_rate?: number
  volume_24h?: number | null
  base_volume_24h?: number | null
  quote_volume_24h?: number | null
  change_pct?: number
}

export interface AnalyzeOIEntry {
  exchange: string
  oi_usd: number
  oi_coins: number
  timestamp: number
}

export interface AnalyzeOrderEntry {
  exchange: string
  side: string
  price: number
  usdt_size: number
  first_seen_ts: number
  last_seen_ts: number
}

export interface AnalyzeDexToken {
  chain: string
  address: string
  liquidity: number
  is_primary: boolean
}

export interface AnalyzeDexPool {
  pair: string
  dex: string
  address: string
  volume_usd?: number
  liquidity_usd?: number
}

export interface AnalyzeAlertSummary {
  exchange: string
  side: string
  count: number
  total_size: number
}

export interface AnalyzeTradeFlowTotals {
  buy_count: number
  sell_count: number
  total_count: number
  buy_match_count: number
  sell_match_count: number
  total_match_count: number
  buy_qty: number
  sell_qty: number
  total_qty: number
  buy_notional: number
  sell_notional: number
  total_notional: number
  net_notional: number
  buy_share_pct: number
}

export interface AnalyzeEpisodeAlertCorrelation {
  type: string
  exchange: string
  side: string
  count: number
  first_ts: number | null
  last_ts: number | null
}

export interface AnalyzeEpisodeCorrelations {
  open_interest: Array<{
    exchange: string
    first_ts: number
    last_ts: number
    first: number
    last: number
    change: number
    change_pct: number | null
    sample_count: number
  }>
  positions: Array<Record<string, unknown>>
  alerts: AnalyzeEpisodeAlertCorrelation[]
  truncated: {
    alert_groups: number
    positions: number
  }
}

export interface AnalyzeTemporalPattern {
  id?: number | string
  episode_key?: string
  exchange: string
  symbol: string
  market_type: string
  side: 'buy' | 'sell' | string
  basis: 'notional' | 'quantity' | string
  value: number | string
  status: 'active' | 'ended'
  pattern_type: string
  algorithm_version?: number | string
  repeat_count: number
  observed_minutes: number
  total_qty: number
  total_notional: number
  first_event_ts?: number | null
  last_event_ts?: number | null
  duration_seconds?: number | null
  cadence_seconds?: number | null
  cadence_jitter_seconds?: number | null
  events_per_cycle?: number | null
  confidence: number
  price_change_pct?: number | null
  quote_currency?: string
  source_event_semantics?: string | null
  dedupe_quality?: string
  quality_flags?: string[]
  provenance?: string | null
  correlations?: AnalyzeEpisodeCorrelations
  summary_text?: string
  end_reason?: string | null
}

export interface AnalyzeTradeFlowVenue extends AnalyzeTradeFlowTotals {
  exchange: string
  market_type: string
  observed_buckets: number
  coverage_ratio: number
  first_minute_ts: number
  latest_minute_ts: number
  price_open: number | null
  price_high: number | null
  price_low: number | null
  price_close: number | null
  first_trade_ts: number | null
  last_trade_ts: number | null
  quote_currency: string
  source_event_semantics: string
  dedupe_quality: string
  quality_flags: string[]
}

export interface AnalyzeTradeOpenInterestObservation {
  coins: number
  ts: number
  origin_ts: number | null
  measurement_from_ts: number
  measurement_to_ts: number
  approximate: boolean
  source: string
  bucket_seconds: number
  quality: string
  max_age_seconds: number
}

export interface AnalyzeTradeOpenInterestVenue {
  exchange: string
  status: 'ready' | 'unavailable' | string
  reason: string | null
  start_coins: number | null
  end_coins: number | null
  change_coins: number | null
  change_pct: number | null
  start_ts: number | null
  end_ts: number | null
  approximate: boolean
  source: string | null
  missing_endpoints?: string[]
  start_observation?: AnalyzeTradeOpenInterestObservation
  end_observation?: AnalyzeTradeOpenInterestObservation
}

export interface AnalyzeTradeOpenInterestWindow {
  status: 'ready' | 'partial' | 'unavailable' | string
  unit: string
  from_ts: number
  to_ts: number
  start_coins: number | null
  end_coins: number | null
  change_coins: number | null
  change_pct: number | null
  matched_venues: number
  total_venues: number
  approximate: boolean
  venues: AnalyzeTradeOpenInterestVenue[]
  reason: string | null
}

export interface AnalyzeTradeStreamHealthRow {
  exchange: string
  symbol: string
  market_type: string
  configured: boolean
  desired: boolean | null
  live: boolean
  status: string
  reported_status: string | null
  reason: string | null
  reason_code: string | null
  last_event_ts: number | null
  last_minute_ts: number | null
  last_write_ts: number | null
  last_healthcheck_ts: number | null
  event_age_seconds: number | null
  write_age_seconds: number | null
  healthcheck_age_seconds: number | null
  received_count: number
  accepted_count: number
  rejected_count: number
  duplicate_count: number
  aggregate_rows: number
  write_failure_count: number
  coverage_ratio: number | null
  quote_currency: string | null
  source_event_semantics: string | null
  dedupe_quality: string | null
  quality_flags: string[]
  provenance: {
    configuration_source: string
    selection_source: string
    state_source: string | null
    config_revision: string | null
    state_created_at: number | null
    state_updated_at: number | null
  }
}

export interface AnalyzeTradeStreamHealth {
  available: boolean
  process_enabled: boolean
  health_scope: 'current_runtime_state' | string
  generated_at: number
  stale_after_seconds: number
  healthcheck_stale_after_seconds: number
  summary: {
    configured_count: number
    desired_count: number
    desired_unknown_count: number
    reported_count: number
    live_count: number
    healthy_count: number
    problem_count: number
    status_counts: Record<string, number>
  }
  exchanges: AnalyzeTradeStreamHealthRow[]
}

export interface AnalyzeTradeFlow {
  status: 'ready' | 'degraded' | 'no_data' | 'legacy' | 'disabled' | string
  ticker: string
  symbol: string
  window_minutes: number
  ago_minutes?: number
  bucket_seconds: number
  count_semantics: 'exchange_native_trade_events'
  from_ts: number
  to_ts: number
  latest_minute_ts: number
  coverage: {
    expected_buckets: number
    observed_buckets: number
    coverage_ratio: number
    venue_count: number
  }
  totals: AnalyzeTradeFlowTotals
  notional_by_currency: Record<string, AnalyzeTradeFlowTotals>
  open_interest_window?: AnalyzeTradeOpenInterestWindow
  size_buckets: Record<string, Record<string, number>>
  venues: AnalyzeTradeFlowVenue[]
  timeline: Array<AnalyzeTradeFlowTotals & { minute_ts: number }>
  pattern_min_repeat: number
  patterns: Array<{
    exchange: string
    market_type: string
    side: 'buy' | 'sell'
    basis: 'notional' | 'quantity'
    value: number
    repeats: number
  }>
  current_patterns: AnalyzeTemporalPattern[]
  historical_patterns: AnalyzeTemporalPattern[]
  current_text: string
  history_text: string
  pattern_display: {
    text_limit_per_status: number
    current_count: number
    historical_count: number
    current_text_omitted: number
    history_text_omitted: number
  }
  pattern_query: {
    from_ts: number
    to_ts: number
    as_of_ts: number
    limit: number
    returned_count: number
    truncated: boolean
    next_cursor: string | null
    pagination_supported: boolean
    truncation_reason: string | null
  }
  correlations: {
    correlation_from: number
    correlation_to: number
    requested_correlation_from: number
    requested_correlation_to: number
    window_clipped: boolean
    open_interest_source: string | null
    open_interest_bucket_seconds: number | null
    open_interest: Array<Record<string, unknown>>
    positions: Array<Record<string, unknown>>
    uncorrelated_positions?: Array<Record<string, unknown> & {
      correlation_status: 'uncorrelated'
      correlation_reason: 'unknown_timestamp' | 'unknown_closed_timestamp'
    }>
    uncorrelated_position_summary?: {
      total: number
      reason_counts: {
        unknown_timestamp: number
        unknown_closed_timestamp?: number
      }
    }
    alerts: AnalyzeEpisodeAlertCorrelation[]
    by_episode: Record<string, AnalyzeEpisodeCorrelations>
    limits: Record<string, number>
    truncated: Record<string, number>
  }
  trade_stream_state: AnalyzeTradeStreamHealth
  quality: {
    temporal_table_available: boolean
    temporal_episode_rows: number
    temporal_query_truncated: boolean
    warnings: string[]
    streams: AnalyzeTradeStreamHealthRow[]
    correlation_sources: Record<string, boolean>
  }
  data_quality: {
    row_count: number
    structured_pattern_rows: number
    legacy_pattern_rows: number
    invalid_pattern_rows: number
    is_stale: boolean
    warnings: string[]
  }
}

export interface TradePatternSettings {
  enabled: boolean
  profile: 'balanced' | 'conservative' | 'sensitive'
  algorithm_version: number
  evaluation_horizons_minutes: number[]
  min_trade_notional_usdt: number
  notional_bucket_usdt: number
  quantity_decimals: number
  periodic_min_occurrences: number
  periodic_min_intervals: number
  periodic_min_seconds: number
  periodic_max_seconds: number
  periodic_min_match_ratio: number
  timing_tolerance_seconds: number
  timing_tolerance_ratio: number
  max_missed_cycle_ratio: number
  burst_window_seconds: number
  burst_min_events: number
  burst_min_consecutive_windows: number
  burst_count_tolerance_ratio: number
  sustained_after_minutes: number
  min_confidence: number
  inactive_confirmation_minutes: number
  allowed_lateness_seconds: number
  max_candidates_per_stream: number
  candidate_ttl_minutes: number
  max_active_patterns_per_symbol: number
  recent_event_limit: number
  minute_bin_limit: number
  active_json_max_bytes: number
  checkpoint_interval_seconds: number
  episode_minute_retention_days: number
  episode_history_retention_days: number
  correlation_grace_minutes: number
}

export interface AnalyzeDeepStatus {
  status: 'idle' | 'pending' | 'running' | 'complete' | 'error'
  job_id?: string | null
  cached_at?: number | null
  updated_at?: number | null
  error?: string | null
  section_status?: Record<string, 'pending' | 'running' | 'complete' | 'error'>
  section_errors?: Record<string, string>
}

export type AnalyzeSections = {
  market_snapshot?: {
    futures: AnalyzeMarketEntry[]
    spot: AnalyzeMarketEntry[]
  }
  open_interest?: {
    exchanges: AnalyzeOIEntry[]
    total_oi_usd: number
    total_oi_coins: number
  }
  orders?: {
    orders: AnalyzeOrderEntry[]
    bid_total: number
    ask_total: number
  }
  position_history?: {
    positions: PositionHistoryEntry[]
    notes: PositionHistoryNote[]
  }
  recent_alerts?: {
    alert_types: Record<string, number>
    orderbook_summary: AnalyzeAlertSummary[]
    total: number
  }
  trade_flow?: AnalyzeTradeFlow
  dex_activity?: {
    tokens_found: number
    primary_chain: string | null
    primary_liquidity: number
    tokens: AnalyzeDexToken[]
    pools: AnalyzeDexPool[]
    transactions?: Record<string, { count: number; total_usd: number; sample: Record<string, unknown>[] }>
    large_transactions?: Record<string, { count: number; total_usd: number; sample: Record<string, unknown>[] }>
    ohlcv?: Record<string, { count: number; total_usd: number; sample: Record<string, unknown>[] }>
  }
  ticker_info?: TickerInfo
  ai_analysis?: {
    text: string
    generated_at?: number
    model?: string
  }
}

export interface AnalyzeResult {
  ticker: string
  timestamp: number
  sections: AnalyzeSections
  deep_status?: AnalyzeDeepStatus
}

export interface AnalyzeStatusResponse extends AnalyzeDeepStatus {
  ticker: string
  sections: Partial<AnalyzeSections>
}
